/**
 * Turning `#channel` in a message into something you can click.
 *
 * The renderer in `@/lib/markdown` must stay a pure function of its text — it
 * is the security boundary for every message body, and it is unit-tested
 * without a router or a store around it. So it does not resolve channel names
 * itself: it emits a small component that asks this context whether the name
 * belongs to a conversation the reader can actually open.
 *
 * With no provider — a preview, a test, a notification body — the answer is
 * "no", and the text renders as plain text. That is also the answer for a
 * channel the reader has no access to, and deliberately the same answer: the
 * client cannot tell a private channel it cannot see from a word that merely
 * starts with a hash, and a distinct "this exists but is closed to you"
 * rendering would announce the existence of rooms the server never disclosed.
 */

import { createContext, useContext, useMemo } from 'react'
import type { ReactElement, ReactNode } from 'react'
import { useNavigate } from 'react-router-dom'

import { useSyncStore } from '@/api/store'
import type { Room } from '@/api/types'

export interface ChannelTarget {
  roomId: string
  /** The channel's own spelling, which may differ from what was typed. */
  slug: string
}

export interface ChannelLinkValue {
  /** The channel the reader may open under this name, or null. */
  resolve: (name: string) => ChannelTarget | null
  open: (roomId: string) => void
}

export const ChannelLinkContext = createContext<ChannelLinkValue | null>(null)

export function useChannelLinks(): ChannelLinkValue | null {
  return useContext(ChannelLinkContext)
}

/** The name a `#reference` may use for a room: its slug, or its display name. */
function namesOf(room: Room): string[] {
  const names: string[] = []
  if (room.slug !== null && room.slug !== '') names.push(room.slug.toLowerCase())
  if (room.name !== null && room.name !== '') names.push(room.name.toLowerCase())
  return names
}

export interface ChannelLinkProviderProps {
  children: ReactNode
}

/**
 * Resolves against every room already in the sync store, which is exactly the
 * set the server has told this account about: the channels it is in, plus the
 * public ones it could join. Nothing else can be named into existence here.
 */
export function ChannelLinkProvider({ children }: ChannelLinkProviderProps): ReactElement {
  const navigate = useNavigate()
  const { rooms } = useSyncStore()

  const value = useMemo<ChannelLinkValue>(() => {
    const byName = new Map<string, ChannelTarget>()

    for (const room of rooms) {
      if (room.kind !== 'channel') continue
      const target: ChannelTarget = { roomId: room.id, slug: room.slug ?? room.name ?? '' }
      for (const name of namesOf(room)) {
        // First match wins, so a room whose slug is another room's display name
        // cannot displace the one that owns the slug.
        if (!byName.has(name)) byName.set(name, target)
      }
    }

    return {
      resolve: (name) => byName.get(name.toLowerCase()) ?? null,
      open: (roomId) => navigate(`/rooms/${encodeURIComponent(roomId)}`),
    }
  }, [navigate, rooms])

  return <ChannelLinkContext.Provider value={value}>{children}</ChannelLinkContext.Provider>
}
